import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { MagNav, ArticleCard, SectionTitle, SubscribeBand, C, serif, sans } from "@/components/magazine/pubShared";
import { RadioPlayerBar } from "@/components/magazine/RadioPlayerBar";

const B = process.env.REACT_APP_BACKEND_URL;

export default function PublicMagazine() {
  const { pubSlug, catSlug } = useParams();
  const [home, setHome] = useState(null);
  const [catArticles, setCatArticles] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${B}/api/mag/public/${pubSlug}/home`).then((r) => setHome(r.data)).catch(() => setErr(true));
  }, [pubSlug]);
  useEffect(() => {
    if (catSlug) axios.get(`${B}/api/mag/public/${pubSlug}/articles`, { params: { category: catSlug } }).then((r) => setCatArticles(r.data.articles));
    else setCatArticles(null);
  }, [pubSlug, catSlug]);

  if (err) return <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg, ...serif }}>Magazine not found.</div>;
  if (!home) return <div className="min-h-screen" style={{ background: C.bg }} />;
  const hero = home.hero;

  return (
    <div className="min-h-screen pb-24" style={{ background: C.bg, ...sans }} data-testid="public-magazine">
      <MagNav pub={home.publication} slug={pubSlug} />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 space-y-16">
        {catSlug ? (
          <section>
            <SectionTitle>{(catArticles?.[0]?.categories?.[0]) || catSlug.replace("-", " ").toUpperCase()}</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {(catArticles || []).map((a) => <ArticleCard key={a.article_id} a={a} slug={pubSlug} />)}
              {catArticles && !catArticles.length && <div style={{ color: C.muted }}>No stories in this section yet.</div>}
            </div>
          </section>
        ) : (
          <>
            {hero && (
              <section className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="mag-pub-hero">
                <Link to={`/mag/${pubSlug}/a/${hero.slug}`} className="lg:col-span-8 group relative block overflow-hidden" style={{ border: `1px solid ${C.line}` }}>
                  {hero.featured_image ? (
                    <img src={hero.featured_image} alt={hero.title} className="w-full h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : <div className="w-full h-[420px]" style={{ background: C.emerald }} />}
                  <div className="absolute inset-0 flex flex-col justify-end p-8" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0.05) 60%)" }}>
                    <div className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-3" style={{ color: "#F4B860" }}>Featured Story</div>
                    <h1 className="text-4xl sm:text-5xl font-black leading-none tracking-tighter text-white" style={serif}>{hero.title}</h1>
                    {hero.subtitle && <p className="mt-3 max-w-2xl text-white/85 text-sm sm:text-base">{hero.subtitle}</p>}
                    <div className="mt-5"><span className="inline-block px-5 py-2.5 text-xs uppercase tracking-[0.2em] font-bold text-white" style={{ background: C.terra }} data-testid="mag-read-story-btn">Read Story</span></div>
                  </div>
                </Link>
                <div className="lg:col-span-4 space-y-6">
                  <div className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: C.terra }}>Most Popular</div>
                  {home.popular.slice(0, 5).map((a, i) => (
                    <Link key={a.article_id} to={`/mag/${pubSlug}/a/${a.slug}`} className="flex gap-4 group">
                      <div className="text-3xl font-black w-8" style={{ ...serif, color: C.line }}>{i + 1}</div>
                      <div>
                        <div className="font-bold leading-snug group-hover:text-[#E05C3A] transition-colors" style={{ ...serif, color: C.ink }}>{a.title}</div>
                        <div className="text-[11px] mt-1" style={{ color: C.muted }}>{(a.categories || [])[0]} · {a.reading_time} min</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
            {!hero && <div className="text-center py-20" style={{ ...serif, color: C.ink2 }}>No stories published yet — check back soon.</div>}

            {home.latest.length > 0 && (
              <section data-testid="mag-section-latest">
                <SectionTitle>Latest Stories</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {home.latest.slice(0, 8).map((a) => <ArticleCard key={a.article_id} a={a} slug={pubSlug} />)}
                </div>
              </section>
            )}
            {Object.entries(home.sections).map(([name, arts]) => arts.length > 0 && (
              <section key={name} data-testid={`mag-section-${name.toLowerCase().replace(/[^a-z]/g, "")}`}>
                <SectionTitle>{name === "Radio" ? `From ${home.radio.station_name || "the Radio"}` : name}</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                  {arts.map((a) => <ArticleCard key={a.article_id} a={a} slug={pubSlug} />)}
                </div>
              </section>
            ))}
          </>
        )}
      </main>
      <SubscribeBand slug={pubSlug} />
      <footer className="border-t py-10 text-center text-xs" style={{ borderColor: C.line, color: C.muted }}>
        © {new Date().getFullYear()} {home.publication.name} — {home.publication.tagline}
      </footer>
      <RadioPlayerBar stationId={home.radio.station_id} />
    </div>
  );
}
